import React, { Component } from 'react';
import { connect } from 'react-redux';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { Button } from 'primereact/button';
import { Toast } from 'primereact/toast';

import { DATATABLE_CONFIG, ZATCA_MONITOR_SUBMENU_ID } from '../../../shared/Config';
import { HASACCESS } from '../../../shared/utility';
import { formatDate, formatDateTime, apiErrorMessage } from './zatcaShared';
import { getQueueSummary, getRejectedInvoices } from './zatcaApi';

const alertStyle = { padding: '0.75em 1em', borderRadius: '4px', background: '#fdecea', border: '1px solid #ef9a9a', color: '#8e1b1b', marginBottom: '1em' };
const clearStyle = { padding: '0.75em 1em', borderRadius: '4px', background: '#e8f5e9', border: '1px solid #81c784', color: '#1b5e20', marginBottom: '1em' };

// How soon before expiry a certificate is flagged.
const CERTIFICATE_WARNING_DAYS = 30;

/**
 * Whether every branch's invoices are reaching ZATCA. Two numbers matter:
 * invoices still pending past ZATCA's 24-hour reporting window, and invoices
 * ZATCA rejected. Both should be zero; anything else needs a person.
 *
 * The data comes from the ZATCA service (ZatcaTest), not the RMS backend.
 */
class ZatcaMonitor extends Component {
    state = {
        access: undefined,
        loading: false,
        summary: [],
        rejected: [],
        refreshedAt: null
    }

    componentDidMount() {
        this.setState({ access: HASACCESS(ZATCA_MONITOR_SUBMENU_ID) });
        this.refresh();
    }

    refresh = () => {
        const companyId = this.props.loggedUserDetail.CompanyId;
        this.setState({ loading: true });

        Promise.all([getQueueSummary(companyId), getRejectedInvoices(companyId)])
            .then(([summary, rejected]) => {
                this.setState({
                    loading: false,
                    summary: Array.isArray(summary.data) ? summary.data : [],
                    rejected: Array.isArray(rejected.data) ? rejected.data : [],
                    refreshedAt: new Date()
                });
            })
            .catch(err => {
                this.setState({ loading: false });
                this.growl.show({ severity: 'error', summary: null, detail: apiErrorMessage(err, 'Could not load ZATCA monitoring data. Is the ZATCA service running?') });
            });
    }

    branchTemplate = (rowData) => (
        <span>{rowData.branchName || '—'}<br /><small>{rowData.commonName}</small></span>
    )

    countTemplate = (field) => (rowData) => {
        const value = rowData[field] || 0;
        if (value === 0) return 0;
        return <Button type="button" label={String(value)} className="p-button-danger p-button-rounded" />;
    }

    certificateTemplate = (rowData) => {
        if (!rowData.certificateValidUntil) return '—';
        const daysLeft = (new Date(rowData.certificateValidUntil) - new Date()) / 86400000;
        const style = daysLeft < CERTIFICATE_WARNING_DAYS ? { color: '#c62828', fontWeight: 600 } : undefined;
        const note = daysLeft < 0 ? 'Expired ' : (daysLeft < CERTIFICATE_WARNING_DAYS ? 'Renew soon · ' : '');
        return <span style={style}>{note}{formatDate(rowData.certificateValidUntil)}</span>;
    }

    chainTemplate = (rowData) => (rowData.chainHead === null || rowData.chainHead === undefined ? '—' : rowData.chainHead)

    issuedTemplate = (rowData) => formatDateTime(rowData.issuedAt)

    reasonTemplate = (rowData) => <span style={{ whiteSpace: 'normal', wordBreak: 'break-word' }}>{rowData.lastError || '—'}</span>

    render() {
        const access = this.state.access;
        if (access !== undefined && access.viewAccess === false) {
            this.props.history.push('/dashboard');
        }

        const pastDeadline = this.state.summary.reduce((total, row) => total + (row.pastDeadline || 0), 0);
        const rejected = this.state.summary.reduce((total, row) => total + (row.rejected || 0), 0);
        const needsAttention = pastDeadline > 0 || rejected > 0;

        return (
            <div className="p-grid padding">
                {this.state.loading && <div className="page-loader"></div>}
                <Toast ref={(el) => this.growl = el} />
                <div className="p-col-12">
                    <div className="card card-w-title" style={{ position: 'relative' }}>
                        <h1>ZATCA Monitor</h1>
                        <div style={{ overflow: 'hidden', marginBottom: '1em' }}>
                            <Button style={{ float: 'right' }} label="Refresh" icon="pi pi-refresh" className="p-button-secondary"
                                disabled={this.state.loading} onClick={this.refresh} />
                            {this.state.refreshedAt && <span>Updated {formatDateTime(this.state.refreshedAt)}</span>}
                        </div>

                        {this.state.refreshedAt && (needsAttention
                            ? <div style={alertStyle}>
                                Needs attention: {pastDeadline} invoice(s) past the 24-hour reporting deadline and {rejected} rejected by ZATCA.
                            </div>
                            : <div style={clearStyle}>All clear: no invoices past the reporting deadline and none rejected.</div>)
                        }

                        <h3>By branch</h3>
                        <DataTable value={this.state.summary} {...DATATABLE_CONFIG}>
                            <Column field="branchName" header="Branch" body={this.branchTemplate} sortable={true} />
                            <Column field="environment" header="Environment" sortable={true} />
                            <Column field="reported" header="Reported" sortable={true} />
                            <Column field="cleared" header="Cleared" sortable={true} />
                            <Column field="pending" header="Pending" sortable={true} />
                            <Column field="rejected" header="Rejected" body={this.countTemplate('rejected')} sortable={true} />
                            <Column field="pastDeadline" header="Past 24h Deadline" body={this.countTemplate('pastDeadline')} sortable={true} />
                            <Column field="chainHead" header="Last Invoice Counter" body={this.chainTemplate} sortable={true} />
                            <Column field="certificateValidUntil" header="Certificate Valid Until" body={this.certificateTemplate} sortable={true} />
                        </DataTable>

                        <h3>Rejected invoices</h3>
                        <p>Each needs a person: fix the cause, then issue a new invoice or a credit note. A rejected invoice is never re-sent.</p>
                        <DataTable value={this.state.rejected} {...DATATABLE_CONFIG} emptyMessage="No rejected invoices">
                            <Column field="branchName" header="Branch" sortable={true} />
                            <Column field="invoiceNumber" header="Invoice" sortable={true} />
                            <Column field="issuedAt" header="Issued" body={this.issuedTemplate} sortable={true} />
                            <Column field="attemptCount" header="Attempts" />
                            <Column field="lastError" header="Reason" body={this.reasonTemplate} />
                        </DataTable>
                    </div>
                </div>
            </div>
        );
    }
}

const mapStateToProps = state => {
    return {
        loggedUserDetail: state.auth.userObject
    }
}

export default connect(mapStateToProps)(ZatcaMonitor);
